/**
 * Types for the onboarding "Engineering Breaks" gallery - a showcase of the
 * execution breaks Intera surfaces across an engineering org by connecting to
 * its tools. This is onboarding demo content (hardcoded English), not the live
 * product.
 */

import type { GanttPlan } from './gantt';

/** Tools Intera connects to and deep-links into. */
export type BreakPlatform =
  | 'jira'
  | 'confluence'
  | 'dynamics-crm'
  | 'slack'
  | 'github'
  | 'pagerduty'
  | 'bmc-helix';

/** Where a suggested action runs - a connected platform, or an Intera-native op. */
export type ActionPlatform = BreakPlatform | 'calendar' | 'intera';

/**
 * Two kinds of break:
 * - `playbook` - a process/pattern break (recurring mistake, wrong estimation,
 *   team not looped in). The majority of what Intera surfaces.
 * - `commitment` - an explicit promise that's now at risk of being broken.
 */
export type BreakCategory = 'playbook' | 'commitment';

/** Playbook sub-type, shown as a tag on playbook cards. */
export type BreakPlaybookType =
  | 'recurring-mistake'
  | 'wrong-estimation'
  | 'team-not-looped-in'
  | 'scope-creep'
  | 'stale-dependency'
  | 'missing-rollback'
  | 'resource-overload';

export type BreakSeverity = 'high' | 'medium' | 'low';

/** How many tools Intera had to correlate to surface the break. Derived from evidence count. */
export type DetectionScope = 'single-source' | 'cross-platform';

export interface BreakEvidence {
  platform: BreakPlatform;
  /** Reference label, e.g. "DATA-842" or "#proj-q3-dashboard". */
  ref: string;
  /** Deep link to where the gap lives right now (the Jira issue, the page, the thread). */
  url: string;
  /** Pull-quote excerpt from the artifact that surfaces the break. */
  quote: string;
  /** Substring of `quote` to highlight in amber - the conflicting claim. */
  highlight?: string;
  /** Attribution line, e.g. "Sarah Chen · yesterday". */
  attribution?: string;
  /** Substring of `attribution` to render bold and in the alert (rose) color - the phrase that carries the break. */
  attributionHighlight?: string;
}

/**
 * Whether a timeline row is the task that already slipped (the cause) or one of
 * the dependents that's affected and needs to be realigned.
 * - `slip` - the task that already moved off the original deadline. Shown
 *   statically with a solid "it slipped" arrow; the action does NOT move it.
 * - `dependent` - an affected item still stuck at the deadline; applying the
 *   action animates it to catch up. This is the default.
 */
export type ActionTimelineRowKind = 'slip' | 'dependent';

/** One work item on an action's before/after timeline. */
export interface ActionTimelineRow {
  /** Item label, e.g. "DATA-842" or "Checkout Launch". */
  label: string;
  /** Optional role shown in the left gutter, e.g. "Slipped" / "Blocked" / "Epic". */
  role?: string;
  /** Index into the timeline axis where the item sits today. */
  fromIndex: number;
  /** Index into the timeline axis where applying the action moves it. */
  toIndex: number;
  /** When true this is the driving item (the cause), highlighted in rose. */
  isPrimary?: boolean;
  /** Slipped cause vs. affected dependent. Defaults to `dependent`. */
  kind?: ActionTimelineRowKind;
  /** Small annotation on the row, e.g. "re-estimated 3× · 8→13→21". */
  note?: string;
}

/**
 * A small before/after schedule the action changes. Tells a three-part story:
 * the original deadline, the task that slipped off it, and the affected
 * dependents - which animate into place when the action is applied.
 */
export interface ActionTimeline {
  /** Ordered axis ticks, e.g. ["Sprint 42", "Sprint 43", "Sprint 44"]. */
  axis: string[];
  /** The items on the axis - one slipped cause plus the affected dependents. */
  rows: ActionTimelineRow[];
  /** Axis index of the original deadline, drawn as a flagged marker. */
  deadlineIndex?: number;
  /** Headline magnitude of the schedule change, e.g. "+10 days" or "1 sprint". */
  delayLabel?: string;
  /** Caption shown BEFORE applying - what the change will do. */
  shiftNote?: string;
  /** Caption shown AFTER applying - what the schedule looks like now. */
  afterNote?: string;
}

export interface SuggestedAction {
  id: string;
  /** Platform this action operates on - drives the row icon. */
  platform: ActionPlatform;
  /** One-line action title, e.g. "Reschedule dependent tickets". */
  title: string;
  /** Supporting detail, e.g. "Shift 4 BI tickets linked to DATA-842 to Sprint 44." */
  detail: string;
  /** Button label before applying, e.g. "Apply" or "Send update". */
  actionLabel: string;
  /** Confirmation label after applying, e.g. "Rescheduled" or "Update sent". */
  appliedLabel: string;
  /** When true the action renders pre-applied, to show the success state in the demo. */
  preApplied?: boolean;
  /** Optional schedule change visualised when the action is applied. */
  timeline?: ActionTimeline;
  /**
   * Rich, detailed Gantt for this action's schedule change. When present it is
   * rendered as the large action-plan panel (preferred over `timeline`).
   */
  gantt?: GanttPlan;
}

export interface EngineeringBreak {
  id: string;
  category: BreakCategory;
  /** Present for `playbook` category. */
  playbookType?: BreakPlaybookType;
  severity: BreakSeverity;
  /**
   * For `playbook` gaps: the expected rule/process that was broken, stated
   * plainly, e.g. "Schema migrations must ship with a rollback plan."
   */
  playbook?: string;
  /** Engineering team the break sits with, e.g. "Payments Platform". */
  team: string;
  /** Card + drawer headline. */
  title: string;
  /** Short one/two-line card description. */
  summary: string;
  /** Longer drawer description of the gap. */
  detail: string;
  /** Relative detection time, e.g. "2h ago". */
  detectedAgo: string;
  /** Evidence artifacts - length 1 means single-source, >1 means cross-platform. */
  evidence: BreakEvidence[];
  /** Intera's suggested action plan for this break. */
  actionPlan: SuggestedAction[];
}

export function detectionScopeOf(
  engineeringBreak: EngineeringBreak
): DetectionScope {
  return engineeringBreak.evidence.length > 1
    ? 'cross-platform'
    : 'single-source';
}
